'use client'

import type { CourseOrder, EventOrder, OrderItem, OrderStatus } from '@mentora/app-shared/orders/order-item'
import type { PriceFormatter } from '../price-formatter'
import { isRefundEligible, refundDeadline } from '@mentora/app-shared/orders/order-item'
import { Badge, Button, Card, CardContent } from '@mentora/ui-components'
import { CalendarDays, GraduationCap, Ticket } from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { CancelOrderDialog } from './cancel-order-dialog'

export interface OrdersPanelProps {
  orders: OrderItem[]
  /** The storefront's currency — orders are priced in it. */
  formatPrice: PriceFormatter
}

const DATE = { year: 'numeric', month: 'short', day: 'numeric' } as const

export function OrdersPanel({ orders, formatPrice }: OrdersPanelProps) {
  const t = useTranslations('orders')

  if (orders.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
        {t('empty')}
      </div>
    )
  }

  return (
    <div className="grid gap-4">
      {orders.map(order => (
        <Card key={`${order.kind}:${order.id}`} className="overflow-hidden">
          <div className="flex flex-col gap-4 p-4 sm:flex-row">
            <OrderImage order={order} />
            <CardContent className="flex min-w-0 flex-1 flex-col gap-2 p-0">
              <div className="flex flex-wrap items-center gap-2">
                <KindBadge kind={order.kind} />
                <StatusBadge status={order.paymentStatus} />
                <Badge variant="default">{formatPrice(order.pricePaidCents)}</Badge>
              </div>
              <h3 className="text-base font-medium leading-tight">{order.title}</h3>
              {order.kind === 'course'
                ? <CourseDetails order={order} />
                : <EventDetails order={order} />}
            </CardContent>
          </div>
        </Card>
      ))}
    </div>
  )
}

function OrderImage({ order }: { order: OrderItem }) {
  const Fallback = order.kind === 'course' ? GraduationCap : Ticket
  return (
    <div className="relative aspect-video w-full shrink-0 overflow-hidden rounded-md bg-muted/40 sm:w-48">
      {order.imageUrl
        ? <Image src={order.imageUrl} alt={order.title} fill className="object-cover" sizes="(max-width: 640px) 100vw, 200px" />
        : (
            <div className="flex h-full items-center justify-center">
              <Fallback className="size-10 text-muted-foreground/40" />
            </div>
          )}
    </div>
  )
}

function PurchasedOn({ order }: { order: OrderItem }) {
  const t = useTranslations('orders')
  const format = useFormatter()
  return (
    <p className="text-xs text-muted-foreground">
      {t('purchasedOn', { date: format.dateTime(new Date(order.completedAt ?? order.createdAt), DATE) })}
    </p>
  )
}

function CourseDetails({ order }: { order: CourseOrder }) {
  const t = useTranslations('orders')
  const format = useFormatter()
  const deadline = refundDeadline(order)
  const eligible = isRefundEligible(order, new Date())

  return (
    <>
      {order.instructorName && <p className="text-xs text-muted-foreground">{order.instructorName}</p>}
      <PurchasedOn order={order} />

      {order.paymentStatus === 'refunded' && (
        <div className="mt-1 space-y-1 rounded-md border border-border/50 bg-muted/30 p-2 text-xs text-muted-foreground">
          {order.refundedAt && (
            <p>{t('refundedOn', { date: format.dateTime(new Date(order.refundedAt), DATE) })}</p>
          )}
          {order.refundReason && (
            <p>
              <span className="font-medium">{t('reasonLabel')}</span>
              {' '}
              {order.refundReason}
            </p>
          )}
        </div>
      )}

      {eligible && deadline && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground" suppressHydrationWarning>
            {t('deadlineNotice', { date: format.dateTime(deadline, DATE) })}
          </span>
          <CancelOrderDialog enrollmentId={order.id} deadline={deadline} />
        </div>
      )}
    </>
  )
}

function EventDetails({ order }: { order: EventOrder }) {
  const t = useTranslations('orders')
  const format = useFormatter()
  return (
    <>
      <p className="text-xs text-muted-foreground">{order.tierName}</p>
      <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <CalendarDays className="size-3.5" aria-hidden />
        {t('eventOn', { date: format.dateTime(new Date(order.eventStartAt), DATE) })}
      </p>
      <PurchasedOn order={order} />
      {order.ticketPath && (
        <div className="mt-2">
          <Button asChild size="sm" className="w-full sm:w-auto">
            <Link href={order.ticketPath}>
              <Ticket className="size-4" aria-hidden />
              {t('seeTicket')}
            </Link>
          </Button>
        </div>
      )}
    </>
  )
}

function KindBadge({ kind }: { kind: OrderItem['kind'] }) {
  const t = useTranslations('orders')
  return <Badge variant="outline">{kind === 'course' ? t('kind.course') : t('kind.event')}</Badge>
}

const STATUS_VARIANT: Record<OrderStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  completed: 'default',
  refunded: 'secondary',
  pending: 'outline',
  failed: 'destructive',
  abandoned: 'secondary',
  cancelled: 'destructive',
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const t = useTranslations('orders')
  // Literal keys, not `status.${x}`: the catalogue-coverage spec can only check literals.
  const label: Record<OrderStatus, string> = {
    pending: t('status.pending'),
    completed: t('status.completed'),
    refunded: t('status.refunded'),
    failed: t('status.failed'),
    abandoned: t('status.abandoned'),
    cancelled: t('status.cancelled'),
  }
  return <Badge variant={STATUS_VARIANT[status]}>{label[status]}</Badge>
}
